import type { Display } from '../../../types';

export const NO_MEMORY_REGION_NAME: string = 'No Memory Region Specified';

export type AssetMemorySeverity = 'error' | 'warning';

export type AssetMemoryBlockName = 'images' | 'fonts';

export type AssetMemoryDiagnostic = {
    severity: AssetMemorySeverity;
    code: string;
    message: string;
    targetName?: string;
    memoryName?: string;
    assetName?: string;
    blockName?: AssetMemoryBlockName;
};

export type ParsedTargetMemoryRegion = {
    name: string;
    size: string;
    sizeBytes: number | null;
    section?: string;
    bandwidth?: string;
    bandwidthBytesPerSecond: number | null;
};

export type ParsedTarget = {
    name: string;
    memories: ParsedTargetMemoryRegion[];
    display: Display;
};

export type AssetMemoryStorageIdentity = {
    key: string;
    label: string;
};

export type AssetMemoryAssetType = 'image' | 'font';

export type AssetMemorySizeStatus = 'resolved' | 'unknown' | 'requires-code-export';

export type AssetMemorySizeSource = 'source-file' | 'png-dimensions' | 'generated-c-array' | 'generated-output-file';

export type AssetMemorySizeEstimate = {
    sizeBytes: number | null;
    status: AssetMemorySizeStatus;
    source?: AssetMemorySizeSource;
    note?: string;
};

export type AssetMemoryAssetReference = {
    assetType: AssetMemoryAssetType;
    blockName: AssetMemoryBlockName;
    tagName: 'data' | 'file' | 'tiny_ttf' | 'bin';
    name: string;
    sourcePath: string;
    attributes: Record<string, string>;
    memoryName: string | null;
    targetNames: string[];
};

export type AssetMemoryReportAsset = AssetMemoryAssetReference & {
    id: string;
    storageKey: string;
    storageLabel: string;
    targetName: string;
    sizeBytes: number;
    sizeStatus: AssetMemorySizeStatus;
    sizeSource?: AssetMemorySizeSource;
    sizeNote?: string;
    accessTimeMs: number | null;
};

export type MemoryRegionReport = {
    targetName: string;
    name: string;
    section?: string;
    bandwidthBytesPerSecond: number | null;
    capacityBytes: number | null;
    usedBytes: number;
    utilization: number | null;
    overflowBytes: number;
    isOverflowing: boolean;
    canOverflow: boolean;
    isUnspecified: boolean;
    assets: AssetMemoryReportAsset[];
};

export type TargetMemoryReport = {
    targetName: string;
    memoryRegions: MemoryRegionReport[];
};

export type AssetMemoryAnalysis = {
    targets: TargetMemoryReport[];
    parsedTargets: ParsedTarget[];
    diagnostics: AssetMemoryDiagnostic[];
};

export type ParsedTargetMemoryResult = {
    targets: ParsedTarget[];
    diagnostics: AssetMemoryDiagnostic[];
};

export type AssetMemoryAnalysisOptions = {
    parseXml?: (xml: string) => Document;
    resolveAssetSizeBytes?: (asset: AssetMemoryAssetReference) => number | AssetMemorySizeEstimate | null | undefined;
};

export type AssetMemoryFileReader = {
    getBinaryFileData(filePath: string, options?: { suppressError?: boolean }): Promise<Uint8Array | null | undefined>;
};

export type AssetMemoryEstimateOptions = AssetMemoryAnalysisOptions &
    AssetMemoryFileReader & {
        projectPath: string;
        preferGeneratedOutput?: boolean;
        getGeneratedAssetPath?: (asset: AssetMemoryAssetReference) => string | null | undefined;
    };
